import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectIsOwner,
  selectMerchantCustomizations,
  selectMerchantTeams,
  setCurrentTeam
} from 'containers/app/appSlice';
import { useCreateCapitalSessionTokenMutation } from './api';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import CapitalComponents from './components/CapitalComponents';
import { selectWebViewTeamId } from 'containers/auth/authSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectCapitalFinancingEnabled } from './capitalSlice';
import routesPath from 'routes/RoutesPath';
import { Loader } from 'components';
import { WebviewFunctions } from 'constants/common';

// This component is used by both webview and non-webview routes, test webview too in case of any changes in future
interface CapitalProps {
  isWebView?: boolean;
}

const Capital: FC<CapitalProps> = ({ isWebView = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const webviewTeamId = useAppSelector(selectWebViewTeamId);
  const isOwner = useAppSelector(selectIsOwner);
  const { teamFeatures } = useAppSelector(selectMerchantCustomizations) ?? {};
  const isCapitalFinancingEnabled = useAppSelector(selectCapitalFinancingEnabled);
  const merchantTeams = useAppSelector(selectMerchantTeams);

  const [createCapitalSessionToken, { isLoading: isTokenLoading }] = useCreateCapitalSessionTokenMutation();
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [isLoadingStripeConnect, setIsLoadingStripeConnect] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [teamCheckComplete, setTeamCheckComplete] = useState(false);

  const clientSecretRef = useRef<string | null>(null);
  const initializationCompleteRef = useRef(false);

  const hasCapitalPermissions = useMemo(() => {
    return isOwner && teamFeatures?.financingEnabled && isCapitalFinancingEnabled;
  }, [isOwner, teamFeatures?.financingEnabled, isCapitalFinancingEnabled]);

  const fetchClientSecret = async () => {
    if (!(currentTeam?.id || webviewTeamId)) return null;

    if (!clientSecretRef.current) {
      try {
        const result = await createCapitalSessionToken({
          teamId: webviewTeamId ? webviewTeamId : currentTeam.id
        }).unwrap();
        clientSecretRef.current = result.capitalFinancingSessionClientSecret;
      } catch (error) {
        if (!hasCapitalPermissions) {
          navigate(routesPath.HOME);
        }
      }
    }

    return clientSecretRef.current;
  };

  // Function to initialize the Stripe Connect instance
  const initializeStripeConnect = async () => {
    if (!(currentTeam?.id || webviewTeamId) || initializationCompleteRef.current) return;
    initializationCompleteRef.current = true;
    try {
      const result = await createCapitalSessionToken({
        teamId: webviewTeamId ? webviewTeamId : currentTeam.id
      }).unwrap();
      clientSecretRef.current = result.capitalFinancingSessionClientSecret;

      const publishableKey = result.stripePublishableKey;

      const connectInstance = await loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret
      });
      setStripeConnectInstance(connectInstance);
    } catch (error) {
      initializationCompleteRef.current = false;
      if (!hasCapitalPermissions) {
        navigate(routesPath.HOME);
      }
    } finally {
      setIsLoadingStripeConnect(false);
    }
  };

  useEffect(
    () => () => {
      if (stripeConnectInstance) stripeConnectInstance.logout();
    },
    []
  );

  const handleTeamChange = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const teamId = searchParams.get('teamId');

    if (!teamId || merchantTeams.length === 0) {
      setTeamCheckComplete(true);
      return;
    }

    const parsedTeamId = +teamId;

    if (currentTeam?.id === parsedTeamId) {
      setTeamCheckComplete(true);
      return;
    }

    const targetTeam = merchantTeams.find(team => team.id === parsedTeamId);

    if (targetTeam) {
      dispatch(setCurrentTeam(targetTeam));
      navigate(location.pathname, { replace: true });
    } else {
      navigate(routesPath.HOME, { replace: true });
    }
    setTeamCheckComplete(true);
  }, [location.search, merchantTeams, currentTeam?.id, dispatch, navigate]);

  useEffect(() => {
    if (isWebView) {
      if (window?.flutter_inappwebview?.callHandler) {
        window.flutter_inappwebview.callHandler(WebviewFunctions.WEBVIEW_LOADED);
      }
      return;
    }

    handleTeamChange();

    if (teamCheckComplete && !hasCapitalPermissions) {
      navigate(routesPath.HOME);
    }
  }, [isWebView, teamCheckComplete, hasCapitalPermissions]);

  useEffect(() => {
    if (currentTeam?.id || webviewTeamId) {
      initializationCompleteRef.current = false;
      initializeStripeConnect();
      setTeamCheckComplete(false);
    }
  }, [currentTeam?.id, webviewTeamId]);

  const reloadCapital = () => {
    // Force component re-render by updating the key
    setRefreshKey(prev => prev + 1);

    if (stripeConnectInstance) {
      stripeConnectInstance.logout();
    }
    clientSecretRef.current = null;
    initializationCompleteRef.current = false;
    initializeStripeConnect();
  };

  const isLoading = isTokenLoading || isLoadingStripeConnect;

  return (
    <div className="h-full w-full px-[30px] pt-5">
      {!webviewTeamId && (
        <div className="mb-6 flex items-center justify-between">
          <div className="text-2xl font-bold text-primaryText">Financing</div>
        </div>
      )}
      <div className="pb-10">
        {isLoading ? (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <Loader />
          </div>
        ) : (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <CapitalComponents reloadCapital={reloadCapital} refreshKey={refreshKey} />
          </ConnectComponentsProvider>
        )}
      </div>
    </div>
  );
};

export default Capital;
